"use client";

import { Suspense, useState, useEffect, useCallback, useMemo } from "react";
import { getBackendAssetUrl } from "@/lib/api-config";
import axios from "axios";
import { useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import Navbar from "./Navbar";
import HeroBanner from "./HeroBanner";
import dynamic from "next/dynamic";

const Footer = dynamic(() => import("./Footer"), { ssr: false });
const FloatingCart = dynamic(() => import("./FloatingCart"), { ssr: false });
const Testimonials = dynamic(() => import("./Testimonials"), { ssr: false });
const Leaderboard = dynamic(() => import("./Leaderboard"), { ssr: false });
const FAQSection = dynamic(() => import("./FAQSection"), { ssr: false });
const CartSidebar = dynamic(() => import("@/components/landing/CartSidebar"), {
  ssr: false,
});
const PaymentModal = dynamic(
  () => import("@/components/landing/PaymentModal"),
  { ssr: false },
);
const LoginModal = dynamic(() => import("./LoginModal"), { ssr: false });
const MobileNumberModal = dynamic(() => import("./MobileNumberModal"), {
  ssr: false,
});
const QrCodeModal = dynamic(() => import("./QrCodeModal"), { ssr: false });

const ScrollReveal = dynamic(() => import("./ScrollReveal"));
const TrustStripes = dynamic(() => import("./TrustStripes"));
const UidVerify = dynamic(() => import("./UidVerify"), { ssr: false });

const ProductSection = dynamic(() => import("./ProductSection"));
import DeferredSection from "./DeferredSection";
import MaintenanceScreen from "./MaintenanceScreen";
import {
  PRODUCT_CATEGORIES,
  isOffer,
  DIAMOND_IDS,
  ID_DIAMOND_IDS,
} from "@/lib/categories";
import {
  GARENA_SHELLS_MAPPING_KEYS,
  KIOSGAMER_MAPPING_KEYS,
} from "@/lib/constants";
import { useCart } from "@/hooks/useCart";
import type { StockInfo, RegionPool } from "@/hooks/useCart";
import { useAppAuth } from "@/hooks/useAppAuth";
import { useStockEngine } from "@/hooks/useStockEngine";
import { useCartContext } from "@/components/CartProvider";
import { Clock, Flame, Sparkles } from "lucide-react";
import {
  RegionIcons,
  type CatalogGameSummary,
} from "@/components/catalog/GamesExplorer";

const OTHER_FREE_FIRE_REGIONS = [
  { name: "Bangladesh", code: "BD", href: "/games/free-fire?region=bd" },
  {
    name: "Global",
    code: "GLOBAL",
    href: "/games/free-fire?region=global",
  },
  { name: "Brazil", code: "BR", href: "/games/free-fire?region=br" },
  {
    name: "Latin America",
    code: "LATAM",
    href: "/games/free-fire?region=latam",
  },
  { name: "Philippines", code: "PH", href: "/games/free-fire?region=ph" },
  { name: "Thailand", code: "TH", href: "/games/free-fire?region=th" },
  { name: "Taiwan", code: "TW", href: "/games/free-fire?region=tw" },
  { name: "Vietnam", code: "VN", href: "/games/free-fire?region=vn" },
] as const;

const LEVEL_ID_MAP: Record<string, number> = {
  lvl_6: 4796,
  lvl_10: 4797,
  lvl_15: 4798,
  lvl_20: 4799,
  lvl_25: 4800,
  lvl_30: 4801,
};

const KIOSGAMER_LEVEL_ID_MAP: Record<string, number> = {
  id_lvl_6: 108532,
  id_lvl_10: 108533,
  id_lvl_15: 108534,
  id_lvl_20: 108535,
  id_lvl_25: 108536,
  id_lvl_30: 108537,
  lvl_6: 108532,
  lvl_10: 108533,
  lvl_15: 108534,
  lvl_20: 108535,
  lvl_25: 108536,
  lvl_30: 108537,
};

const getLevelIdMap = (isIdRegion: boolean) =>
  isIdRegion ? KIOSGAMER_LEVEL_ID_MAP : LEVEL_ID_MAP;

// Only query-string handling needs client rendering on the static landing page.
// Keep its Suspense boundary below the hero instead of suspending the whole UI.
function LandingQueryParams({
  onChange,
}: {
  onChange: (params: URLSearchParams) => void;
}) {
  const params = useSearchParams();
  useEffect(() => {
    onChange(new URLSearchParams(params.toString()));
  }, [params, onChange]);
  return null;
}

const formatProduct = (
  p: any,
  locale?: string,
  offerMap?: Record<string, any>,
) => {
  const offer = offerMap?.[p.id];
  return {
    ...p,
    name: p.name,
    price: `${locale === "si" ? "රු." : locale === "ta" ? "ரூ." : "Rs."}${(offer?.discountedPrice ?? p.websitePriceLkr ?? p.priceLkr)?.toFixed(2) || "0.00"}`,
    image: p.imageUrl
      ? p.imageUrl.startsWith("http")
        ? p.imageUrl
        : p.imageUrl
      : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23888888"><path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm-10 7H8v3H6v-3H3v-2h3V8h2v3h3v2zm4.5 2c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm4-3c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/></svg>',
    id: p.id,
    _offer: offer || null,
  };
};

interface NewUiLandingProps {
  initialProducts?: any[];
  initialStockData?: any;
  initialMaintenance?: { maintenanceMode?: boolean };
  initialPaymentConfig?: any;
  initialCharges?: {
    processingChargeEnabled: boolean;
    processingChargePercent: number;
  };
  initialOffers?: Record<string, any>;
  initialGames?: CatalogGameSummary[];
  nonce?: string;
  locale?: "en" | "si" | "ta";
}

export function OfferCountdownBanner({ offer }: { offer: any }) {
  const [now, setNow] = useState(Date.now());
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);

    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      clearInterval(timer);
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  const endTime = new Date(offer.endTime).getTime();
  const diff = Math.max(0, endTime - now);
  if (diff <= 0) return null;

  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const pct = offer.products?.[0]?.discountPercentage || 0;

  const segments = [
    { label: "Days", value: days },
    { label: "Hrs", value: hours },
    { label: "Min", value: mins },
    { label: "Sec", value: secs },
  ];

  const validMobile =
    offer.backgroundImageMobile &&
    typeof offer.backgroundImageMobile === "string" &&
    !offer.backgroundImageMobile.startsWith("blob:")
      ? offer.backgroundImageMobile
      : null;
  const validDesktop =
    offer.backgroundImageDesktop &&
    typeof offer.backgroundImageDesktop === "string" &&
    !offer.backgroundImageDesktop.startsWith("blob:")
      ? offer.backgroundImageDesktop
      : null;

  const bgImage = isMobile
    ? validMobile || validDesktop || "/assets/limited.webp"
    : validDesktop || validMobile || "/assets/limited.webp";

  const textColor = offer.textColor || "#1e3a8a";
  const getAlphaColor = (hex: string, alpha: number) => {
    if (hex && hex.startsWith("#")) {
      const cleanHex = hex.replace("#", "");
      let r = 30,
        g = 58,
        b = 138; // default to blue-900
      if (cleanHex.length === 3) {
        r = parseInt(cleanHex[0] + cleanHex[0], 16);
        g = parseInt(cleanHex[1] + cleanHex[1], 16);
        b = parseInt(cleanHex[2] + cleanHex[2], 16);
      } else if (cleanHex.length === 6) {
        r = parseInt(cleanHex.substring(0, 2), 16);
        g = parseInt(cleanHex.substring(2, 4), 16);
        b = parseInt(cleanHex.substring(4, 6), 16);
      }
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return hex;
  };

  return (
    <div
      className="relative w-full rounded-2xl px-5 sm:px-8 py-6 md:py-0 md:aspect-[970/227] mb-6 flex items-center overflow-hidden bg-cover bg-[30%_center] md:bg-center bg-no-repeat group transition-all duration-300"
      style={{
        backgroundImage: `url('${getBackendAssetUrl(bgImage)}')`,
        backgroundColor: offer.bgColor || "transparent",
      }}
    >
      {/* Mobile background banner */}
      {offer.bgColor && (
        <div
          className="absolute inset-0 pointer-events-none z-0 transition-opacity duration-300"
          style={{
            backgroundColor: offer.bgColor,
            opacity:
              offer.bgOpacity !== undefined &&
              offer.bgOpacity !== null &&
              offer.bgOpacity !== ""
                ? Number(offer.bgOpacity)
                : 0,
          }}
        />
      )}

      <div className="relative z-10 w-full flex flex-col sm:flex-row items-center justify-between gap-5">
        <div className="flex items-center gap-4 w-full sm:w-auto md:pl-8 lg:pl-12">
          <div
            className="flex items-center justify-center w-14 h-14 rounded-full shrink-0 shadow-md"
            style={{
              backgroundColor: getAlphaColor(textColor, 0.1),
              color: textColor,
            }}
          >
            <Clock className="w-7 h-7 animate-pulse" />
          </div>
          <div className="space-y-1.5 font-display text-left">
            <h3
              className="text-2xl md:text-3xl lg:text-4xl font-black tracking-wide uppercase"
              style={{ color: textColor }}
            >
              {offer.name}
            </h3>
            {pct > 0 && (
              <p
                className="text-xs md:text-sm lg:text-base font-black flex items-center flex-wrap gap-1"
                style={{ color: getAlphaColor(textColor, 0.95) }}
              >
                Up to{" "}
                <span
                  className="font-black text-sm md:text-base px-2 py-0.5 rounded-md border font-display shadow-sm"
                  style={{
                    backgroundColor: getAlphaColor(textColor, 0.2),
                    color: textColor,
                    borderColor: getAlphaColor(textColor, 0.25),
                  }}
                >
                  {pct}% OFF
                </span>{" "}
                — Grab yours before it ends!
              </p>
            )}
          </div>
        </div>

        <div
          className="flex items-center justify-center gap-2.5 shrink-0 backdrop-blur-[1px] px-6 py-4 rounded-2xl border shadow-[0_0_15px_rgba(59,130,246,0.1),_inset_0_0_10px_rgba(59,130,246,0.05)] md:absolute md:right-[1%] md:top-1/2 md:-translate-y-1/2"
          style={{
            backgroundColor: getAlphaColor(textColor, 0.05),
            borderColor: getAlphaColor(textColor, 0.3),
          }}
        >
          {segments.map((seg, i) => (
            <div key={seg.label} className="flex items-center">
              {i > 0 && (
                <span
                  className="font-bold self-start mt-0.5 mx-1.5 animate-pulse"
                  style={{ color: textColor }}
                >
                  :
                </span>
              )}
              <div className="flex flex-col items-center min-w-[48px] sm:min-w-[56px]">
                <span
                  className="text-3xl sm:text-4xl font-extrabold font-display tracking-tight leading-none"
                  style={{ color: textColor }}
                >
                  {String(seg.value).padStart(2, "0")}
                </span>
                <span
                  className="text-[9px] sm:text-[10px] uppercase tracking-widest font-extrabold mt-2"
                  style={{ color: getAlphaColor(textColor, 0.9) }}
                >
                  {seg.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const NewUiLanding = ({
  initialProducts = [],
  initialStockData = {},
  initialMaintenance = {},
  initialPaymentConfig = null,
  initialCharges = {
    processingChargeEnabled: false,
    processingChargePercent: 0,
  },
  initialOffers = {},
  nonce,
  locale = "en",
}: NewUiLandingProps) => {
  const auth = useAppAuth();
  const API_BASE = "/api";

  const {
    uid,
    setUid,
    validationResult,
    setValidationResult,
    selectedTabRegion,
    setSelectedTabRegion,
    purchasedLevels,
    setPurchasedLevels,
    channelLevelUps,
    setChannelLevelUps,
    hasOneTimeOffer,
    setHasOneTimeOffer,
    products,
    stockAvailability,
    regionPools,
    charges,
    paymentConfig,
    hydrateCatalog,
    activeOffers,
  } = useCartContext();

  const [mounted, setMounted] = useState(false);

  // Hydrate global context on mount using SSR-fetched data
  useEffect(() => {
    setMounted(true);
    const stockMap: Record<string, StockInfo> = {};
    if (initialStockData?.products) {
      for (const p of initialStockData.products) {
        stockMap[p.productId] = {
          isAvailable: p.isAvailable,
          shellsNeeded: p.shellsNeeded,
          shellsSg: p.shellsSg ?? 0,
          shellsMy: p.shellsMy ?? 0,
          shellsId: p.shellsId ?? 0,
          maxShellsInSingleAccount: p.maxShellsInSingleAccount,
          totalAvailableShells: p.totalAvailableShells,
          defaultRegion: p.defaultRegion,
          maxQty: p.maxQty ?? (p.isAvailable ? 99 : 0),
          isBundle: p.isBundle ?? false,
          constituents: p.constituents ?? undefined,
        };
      }
    }
    const poolsMap: Record<string, RegionPool> = {};
    if (initialStockData?.summary?.regions) {
      for (const [region, info] of Object.entries(
        initialStockData.summary.regions as Record<string, any>,
      )) {
        poolsMap[region] = {
          accounts: info.accounts ?? 0,
          totalShells: info.totalShells ?? 0,
          maxAccountShells: info.maxAccountShells ?? 0,
          accountShells: info.accountShells ?? [],
          publicReserved: info.publicReserved ?? 0,
        };
      }
    }
    hydrateCatalog({
      products: initialProducts,
      stockAvailability: stockMap,
      regionPools: poolsMap,
      charges: initialCharges,
      paymentConfig: initialPaymentConfig,
      activeOffers: initialOffers,
    });
  }, [
    initialProducts,
    initialStockData,
    initialCharges,
    initialPaymentConfig,
    initialOffers,
    hydrateCatalog,
  ]);

  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [offersList, setOffersList] = useState<any[]>([]);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(
    !!initialMaintenance.maintenanceMode,
  );
  const [isCheckingMaintenance, setIsCheckingMaintenance] = useState(false);
  const [searchParams, setSearchParams] = useState(() => new URLSearchParams());

  // Handle auto-login trigger from query param (e.g. from /login redirect)
  useEffect(() => {
    const loginParam = searchParams.get("login");
    if (loginParam === "true" && !auth.isAuthenticated && !auth.isAuthLoading) {
      setIsLoginModalOpen(true);

      // Clean up the URL to remove the login param after processing
      const newUrl = window.location.pathname;
      window.history.replaceState({ ...window.history.state }, "", newUrl);
    }
  }, [searchParams, auth.isAuthenticated, auth.isAuthLoading]);

  // Fetch active limited-time offers
  useEffect(() => {
    axios
      .get(`${API_BASE}/public/active-offers`)
      .then((res) => {
        const data = res.data?.data || res.data || [];
        setOffersList(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("landing-scrollbar");
    return () => {
      document.documentElement.classList.remove("landing-scrollbar");
    };
  }, []);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null);
  const [isSubmittingOrder, setIsSubmittingOrder] = useState(false);

  const hasValidPlayer = !!(
    validationResult &&
    (validationResult.valid || validationResult.nickname)
  );

  const cartHook = useCart(
    products,
    stockAvailability,
    0,
    false,
    charges,
    activeOffers,
  );
  const playerRegion = selectedTabRegion;
  const stockEngine = useStockEngine(
    cartHook.cart,
    stockAvailability,
    regionPools,
    playerRegion,
  );

  const handleValidatorReset = () => {
    setValidationResult(null);
    setUid("");
    cartHook.clearCart();
    setChannelLevelUps([]);
    setPurchasedLevels({});
    setHasOneTimeOffer(null);
    setSelectedTabRegion("SG");
  };

  const scrollToUid = useCallback(() => {
    const section = document.getElementById("uid-verify");
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "center" });
      const input = section.querySelector("input");
      if (input) {
        setTimeout(() => {
          input.focus();
          input.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
          setTimeout(() => {
            input.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
          }, 1800);
        }, 600);
      }
    }
  }, []);

  const onNoPlayer = hasValidPlayer ? undefined : scrollToUid;

  useEffect(() => {
    const selectProductId = searchParams.get("selectProduct");
    if (selectProductId && products.length > 0) {
      const prod = products.find(
        (p) => String(p.id) === String(selectProductId),
      );
      if (prod) {
        // Auto-switch region tab if the product belongs to ID region
        const isIdItem = KIOSGAMER_MAPPING_KEYS.includes(prod.id);
        setSelectedTabRegion(isIdItem ? "ID" : "SG");

        cartHook.addToCart(prod.id, {
          qty: 1,
          item_id: prod.item_id || prod.id,
          isChannelItem: false,
        });

        // Clean URL
        const newUrl = window.location.pathname;
        window.history.replaceState({ ...window.history.state }, "", newUrl);

        // Smooth scroll to UID section to prompt checkout
        scrollToUid();
      }
    }
  }, [searchParams, products, cartHook, scrollToUid]);

  useEffect(() => {
    const fetchMaintenance = async () => {
      try {
        const resp = await axios.get(`${API_BASE}/public/maintenance`);
        const data = resp.data?.data || resp.data;
        if (data && data.maintenanceMode !== undefined) {
          setIsMaintenanceMode(data.maintenanceMode);
        }
      } catch (e) {
        console.error("Failed to fetch maintenance status:", e);
      }
    };

    const maintenanceInterval = setInterval(fetchMaintenance, 30000);
    fetchMaintenance();

    return () => {
      clearInterval(maintenanceInterval);
    };
  }, [API_BASE]);

  const buildOrderDetails = useCallback(() => {
    const items = Object.entries(cartHook.cart).map(([id, item]) => ({
      itemId: id,
      quantity: item.qty,
    }));

    return {
      playerId: validationResult?.userId || validationResult?.uid || uid,
      region: validationResult?.region || "SG",
      playerName: validationResult?.nickname || "",
      items,
      ...(auth.email ? { userEmail: auth.email } : {}),
      ...(auth.firebaseUser
        ? {
            firebaseUid: auth.firebaseUser.uid,
          }
        : auth.oidcAuth?.user?.profile?.sub
          ? {
              firebaseUid: auth.oidcAuth.user.profile.sub,
            }
          : {}),
    };
  }, [cartHook.cart, validationResult, uid, auth]);

  const [pendingGenieOrder, setPendingGenieOrder] = useState<any>(null);
  const [pendingCeypayOrder, setPendingCeypayOrder] = useState<any>(null);

  const handleGenieCheckout = async (mode: "CARD" | "QR" = "CARD") => {
    setIsSubmittingOrder(true);
    try {
      const orderDetails = buildOrderDetails();
      const resp = await axios.post(`${API_BASE}/payment/public-order`, {
        gateway: "GENIE",
        orderDetails: {
          ...orderDetails,
          paymentMethod: mode === "QR" ? "QR" : undefined,
        },
      });
      const data = resp.data.data || resp.data;

      if (data.paymentUrl || data.qrCode) {
        if (mode === "CARD" && data.paymentUrl) {
          window.location.href = data.paymentUrl;
        } else {
          setPendingGenieOrder(data);
          setShowPaymentModal(false);
        }
      }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || "Failed to initialize payment");
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleBcoinCheckout = async () => {
    setIsSubmittingOrder(true);
    try {
      const orderDetails = buildOrderDetails();
      const token = await auth.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${API_BASE}/payment/public-order-with-bcoins`,
        { orderDetails },
        { headers },
      );
      const data = resp.data.data || resp.data;
      const statusId =
        data?.id ??
        data?.transactionId ??
        data?.batchId ??
        data?.orderBatchId ??
        null;

      if (!statusId) {
        throw new Error("Order placed but missing payment status id");
      }

      cartHook.clearCart();
      setShowPaymentModal(false);
      window.location.href = `/payment-status?id=${encodeURIComponent(String(statusId))}`;
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || "Failed to initialize BCoins payment");
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleEzCashCheckout = async (referenceNumber: string) => {
    try {
      const orderDetails = buildOrderDetails();
      const token = await auth.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const resp = await axios.post(
        `${API_BASE}/payment/public-order-with-ezcash`,
        { orderDetails, referenceNumber },
        { headers },
      );
      const data = resp.data.data || resp.data;
      return data;
    } catch (e: any) {
      throw e;
    }
  };

  const handleCeypayCheckout = async (
    provider: "BINANCE" | "KUCOIN" | "BYBIT",
  ) => {
    setIsSubmittingOrder(true);
    try {
      const orderDetails = buildOrderDetails();
      const resp = await axios.post(`${API_BASE}/payment/public-order`, {
        gateway: "CEYPAY",
        orderDetails: {
          ...orderDetails,
          paymentMethod: provider,
        },
      });
      const data = resp.data.data || resp.data;

      if (data.qrCode) {
        setPendingCeypayOrder(data);
        setShowPaymentModal(false);
      } else if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else {
        alert("Payment link received. Please check your order status.");
        window.location.href = `/payment-status?id=${data.id}`;
      }
    } catch (e: any) {
      console.error(e);
      alert(e.response?.data?.message || "Failed to initialize payment");
    } finally {
      setIsSubmittingOrder(false);
    }
  };

  const handleCheckout = () => {
    if (!auth.isAuthenticated) {
      setIsLoginModalOpen(true);
      return;
    }

    // Check if mobile number is required
    if (auth.hasMobile === false) {
      setIsMobileModalOpen(true);
      return;
    }

    setSelectedPaymentMethod(null);
    setShowPaymentModal(true);
  };

  useEffect(() => {
    if (
      !validationResult ||
      (!validationResult.valid && !validationResult.nickname)
    )
      return;

    const playerReg = (validationResult?.region || "").toUpperCase();
    const isSgMyId = ["SG", "MY", "LK", "SRILANKA", "SRI LANKA", "ID"].includes(playerReg);
    if (!isSgMyId) return;

    const eligibleIds = new Set<number>(
      channelLevelUps.map((item) => Number(item.item_id)),
    );

    cartHook.setCart((prev) => {
      const updated = { ...prev };
      let changed = false;

      const levelMap = getLevelIdMap(validationResult?.region === "ID");
      Object.keys(updated).forEach((cartItemId) => {
        const garenaId = levelMap[cartItemId];
        if (!garenaId) return;
        const isPurchased =
          purchasedLevels[garenaId] === true ||
          purchasedLevels[String(garenaId)] === true;
        const isIneligible = !eligibleIds.has(garenaId);
        if (isPurchased || isIneligible) {
          delete updated[cartItemId];
          changed = true;
        }
      });

      Object.keys(updated).forEach((cartItemId) => {
        const info = stockAvailability[cartItemId];
        if (!info?.isBundle || !info.constituents) return;
        const hasUnavailableConstituent = info.constituents.some((c) => {
          const garenaId = levelMap[c.productId];
          if (!garenaId) return false;
          const isPurchased =
            purchasedLevels[garenaId] === true ||
            purchasedLevels[String(garenaId) as any] === true;
          const isIneligible = !eligibleIds.has(garenaId);
          return isPurchased || isIneligible;
        });
        if (hasUnavailableConstituent) {
          delete updated[cartItemId];
          changed = true;
        }
      });

      return changed ? updated : prev;
    });
  }, [
    validationResult,
    purchasedLevels,
    channelLevelUps,
    cartHook,
    stockAvailability,
  ]);

  const isIdRegion = playerRegion === "ID";

  const offersCat = PRODUCT_CATEGORIES.find((c) => c.key === "offers");
  const subsCat = PRODUCT_CATEGORIES.find((c) => c.key === "subscriptions");
  const diamondsCat = PRODUCT_CATEGORIES.find((c) => c.key === "diamonds");
  const levelUpsCat = PRODUCT_CATEGORIES.find((c) => c.key === "level_ups");

  const garenaKeySet = useMemo(() => new Set(GARENA_SHELLS_MAPPING_KEYS), []);
  const kiosgamerKeySet = useMemo(() => new Set(KIOSGAMER_MAPPING_KEYS), []);
  const diamondKeySet = useMemo(
    () => new Set(DIAMOND_IDS as readonly string[]),
    [],
  );
  const idDiamondKeySet = useMemo(
    () => new Set(ID_DIAMOND_IDS as readonly string[]),
    [],
  );

  const SUBS_SORT_ORDER: Record<string, number> = {
    lite: 1,
    weekly: 2,
    monthly: 3,
    id_weekly: 2,
    id_monthly: 3,
    id_bp_card: 4,
  };

  const regionFilteredProducts = useMemo(() => {
    return products.filter((p) => {
      // 1. Bundle products
      if (p.type === "BUNDLE") {
        if (isIdRegion) return p.defaultRegion === "ID";
        return p.defaultRegion !== "ID";
      }

      // 2. Indonesia (ID) Region
      if (isIdRegion) {
        if (
          p.defaultRegion === "ID" ||
          p.id?.startsWith("id_") ||
          kiosgamerKeySet.has(p.id) ||
          idDiamondKeySet.has(p.id) ||
          p.kiosgamerItemId != null
        ) {
          return true;
        }

        const isExplicitId = p.shellsId != null;
        const isExplicitSg =
          garenaKeySet.has(p.id) ||
          diamondKeySet.has(p.id) ||
          p.defaultRegion === "SG" ||
          p.defaultRegion === "MY";

        if (isOffer(p)) {
          return p.defaultRegion === "ID" || (!isExplicitSg && isExplicitId);
        }

        return isExplicitId && !isExplicitSg;
      }

      // 3. Singapore / Malaysia (SG/MY) Region
      // Strictly exclude any Indonesia product
      const isIdProduct =
        kiosgamerKeySet.has(p.id) ||
        idDiamondKeySet.has(p.id) ||
        p.id?.startsWith("id_") ||
        p.defaultRegion === "ID";

      if (isIdProduct) {
        return false;
      }

      if (isOffer(p)) {
        return p.defaultRegion !== "ID";
      }

      return (
        garenaKeySet.has(p.id) ||
        diamondKeySet.has(p.id) ||
        p.shellsSg != null ||
        p.shellsMy != null ||
        p.defaultRegion === "SG" ||
        p.defaultRegion === "MY" ||
        !p.defaultRegion
      );
    });
  }, [
    products,
    isIdRegion,
    garenaKeySet,
    kiosgamerKeySet,
    diamondKeySet,
    idDiamondKeySet,
  ]);

  const offers = useMemo(() => {
    // Garena's one-time offers are player-specific. Keep them visible before
    // verification so the customer can discover them, but remove them as soon
    // as a verified player is confirmed to be ineligible.
    const isVerified =
      validationResult?.valid === true || Boolean(validationResult?.nickname);
    if (isVerified && hasOneTimeOffer === false) return [];

    return regionFilteredProducts
      .filter((p) => offersCat?.match(p))
      .map((p) => formatProduct(p, locale, activeOffers))
      .sort((a, b) => {
        const priceA = a.websitePriceLkr ?? a.priceLkr ?? 0;
        const priceB = b.websitePriceLkr ?? b.priceLkr ?? 0;
        return priceA - priceB;
      });
  }, [
    regionFilteredProducts,
    offersCat,
    locale,
    activeOffers,
    validationResult,
    hasOneTimeOffer,
  ]);

  useEffect(() => {
    const isVerified =
      validationResult?.valid === true || Boolean(validationResult?.nickname);
    if (!isVerified || hasOneTimeOffer !== false) return;

    // Do not leave an ineligible one-time offer in the cart after switching
    // or verifying a player ID.
    cartHook.setCart((currentCart) => {
      const nextCart = { ...currentCart };
      let changed = false;

      for (const product of regionFilteredProducts) {
        if (offersCat?.match(product) && nextCart[product.id]) {
          delete nextCart[product.id];
          changed = true;
        }
      }

      return changed ? nextCart : currentCart;
    });
  }, [
    validationResult,
    hasOneTimeOffer,
    regionFilteredProducts,
    offersCat,
    cartHook,
  ]);

  const subscriptions = useMemo(() => {
    const list = regionFilteredProducts
      .filter((p) => subsCat?.match(p))
      .map((p) => formatProduct(p, locale, activeOffers));

    return list.sort((a, b) => {
      const orderA = SUBS_SORT_ORDER[a.id] ?? 99;
      const orderB = SUBS_SORT_ORDER[b.id] ?? 99;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      const priceA = a.websitePriceLkr ?? a.priceLkr ?? 0;
      const priceB = b.websitePriceLkr ?? b.priceLkr ?? 0;
      return priceA - priceB;
    });
  }, [regionFilteredProducts, subsCat, locale, activeOffers, SUBS_SORT_ORDER]);

  const eligibleGarenaIds = new Set<number>(
    channelLevelUps.map((item) => Number(item.item_id)),
  );

  const bundleUnavailableIds = useMemo(() => {
    const unavailable = new Set<string>();
    if (
      !validationResult ||
      (!validationResult.valid && !validationResult.nickname)
    )
      return unavailable;
    const playerReg = (validationResult?.region || "").toUpperCase();
    const isSgMyId =
      isIdRegion ||
      ["SG", "MY", "LK", "SRILANKA", "SRI LANKA", "ID"].includes(playerReg);
    for (const [productId, info] of Object.entries(stockAvailability)) {
      if (!info.isBundle || !info.constituents) continue;
      const hasUnavailableConstituent = info.constituents.some((c) => {
        if (isSgMyId) {
          const garenaId = getLevelIdMap(isIdRegion)[c.productId];
          if (!garenaId) return false;
          const isPurchased =
            purchasedLevels[garenaId] === true ||
            purchasedLevels[String(garenaId) as any] === true;
          const isIneligible = !eligibleGarenaIds.has(garenaId);
          return isPurchased || isIneligible;
        }
        return false;
      });
      if (hasUnavailableConstituent) unavailable.add(productId);
    }
    return unavailable;
  }, [stockAvailability, validationResult, purchasedLevels, eligibleGarenaIds, isIdRegion]);

  const effectiveStockAvailability = useMemo(() => {
    if (bundleUnavailableIds.size === 0) return stockAvailability;
    const updated = { ...stockAvailability };
    Array.from(bundleUnavailableIds).forEach((id) => {
      if (updated[id])
        updated[id] = { ...updated[id], isAvailable: false, maxQty: 0 };
    });
    return updated;
  }, [stockAvailability, bundleUnavailableIds]);

  const diamonds = useMemo(() => {
    return regionFilteredProducts
      .filter((p) => diamondsCat?.match(p))
      .map((p) => formatProduct(p, locale, activeOffers))
      .sort((a, b) => {
        const priceA = a.websitePriceLkr ?? a.priceLkr ?? 0;
        const priceB = b.websitePriceLkr ?? b.priceLkr ?? 0;
        return priceA - priceB;
      });
  }, [regionFilteredProducts, diamondsCat, locale, activeOffers]);

  const LEVEL_UP_SORT_ORDER: Record<string, number> = useMemo(
    () => ({
      lvl_6: 1,
      lvl_10: 2,
      lvl_15: 3,
      lvl_20: 4,
      lvl_25: 5,
      lvl_30: 6,
      id_lvl_6: 1,
      id_lvl_10: 2,
      id_lvl_15: 3,
      id_lvl_20: 4,
      id_lvl_25: 5,
      id_lvl_30: 6,
    }),
    [],
  );

  const levelUpPasses = useMemo(() => {
    return regionFilteredProducts
      .filter((p) => levelUpsCat?.match(p))
      .map((p) => formatProduct(p, locale, activeOffers))
      .map((product) => {
        if (!validationResult) return product;
        const playerReg = (validationResult?.region || "").toUpperCase();
        const isSgMyId =
          isIdRegion ||
          ["SG", "MY", "LK", "SRILANKA", "SRI LANKA", "ID"].includes(playerReg);
        const levelGarenaId = isSgMyId
          ? getLevelIdMap(isIdRegion)[product.id]
          : undefined;
        const isPurchased = levelGarenaId
          ? purchasedLevels[levelGarenaId] === true ||
            purchasedLevels[String(levelGarenaId) as any] === true
          : false;
        const isEligible = levelGarenaId
          ? eligibleGarenaIds.has(levelGarenaId)
          : true;

        let disabledReason = undefined;
        if (isPurchased) {
          disabledReason = "Already Owned";
        } else if (!isEligible) {
          disabledReason = "Not Eligible";
        }

        return {
          ...product,
          isPurchased,
          disabledReason,
        };
      })
      .sort((a, b) => {
        const orderA = LEVEL_UP_SORT_ORDER[a.id] ?? 99;
        const orderB = LEVEL_UP_SORT_ORDER[b.id] ?? 99;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        const priceA = a.websitePriceLkr ?? a.priceLkr ?? 0;
        const priceB = b.websitePriceLkr ?? b.priceLkr ?? 0;
        return priceA - priceB;
      });
  }, [
    regionFilteredProducts,
    levelUpsCat,
    locale,
    activeOffers,
    validationResult,
    isIdRegion,
    purchasedLevels,
    eligibleGarenaIds,
    LEVEL_UP_SORT_ORDER,
  ]);

  const isMainScreenBlocked =
    isMaintenanceMode &&
    !auth.isAdmin &&
    !auth.userGroups.includes("superadmin");

  return (
    <div className="bg-background overflow-x-clip">
      <Suspense fallback={null}>
        <LandingQueryParams onChange={setSearchParams} />
      </Suspense>
      {isMainScreenBlocked ? (
        <MaintenanceScreen onLoginClick={() => setIsLoginModalOpen(true)} />
      ) : (
        <>
          <Navbar
            onLoginClick={() => setIsLoginModalOpen(true)}
            locale={locale}
          />
          <HeroBanner locale={locale} />
          <TrustStripes locale={locale} />

          <ScrollReveal>
            <UidVerify
              uid={uid}
              setUid={setUid}
              validationResult={validationResult}
              setValidationResult={setValidationResult}
              setPurchasedLevels={setPurchasedLevels}
              setChannelLevelUps={setChannelLevelUps}
              setHasOneTimeOffer={setHasOneTimeOffer}
              onReset={handleValidatorReset}
              locale={locale}
            />
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="flex justify-center px-4">
              <div className="w-full max-w-6xl rounded-lg border border-border/70 bg-card/70 p-3 shadow-sm sm:p-4">
                <div className="mb-3 flex items-end justify-between gap-3 px-1">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Choose your region
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Select the region linked to your Free Fire account
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  <button
                    type="button"
                    onClick={() => {
                      if (!hasValidPlayer && selectedTabRegion !== "SG") {
                        setSelectedTabRegion("SG");
                        cartHook.clearCart();
                      }
                    }}
                    disabled={hasValidPlayer}
                    className={`flex min-h-12 items-center justify-start gap-2 rounded-md border px-3 py-2.5 text-left text-xs font-semibold transition-all duration-200 ${
                      (mounted ? selectedTabRegion : "SG") === "SG"
                        ? "border-primary bg-primary text-primary-foreground shadow-sm"
                        : "border-border/70 bg-background text-foreground hover:border-primary/40 hover:bg-accent/50"
                    } ${hasValidPlayer ? "cursor-default opacity-80" : "cursor-pointer"}`}
                  >
                    <RegionIcons code="SG/MY/LK" />
                    <span>SG/MY/LK</span>
                  </button>
                  <Link
                    href="/games/free-fire?region=indonesia"
                    className="flex min-h-12 items-center justify-start gap-2 rounded-md border border-border/70 bg-background px-3 py-2.5 text-left text-xs font-semibold text-foreground transition-all duration-200 hover:border-primary/40 hover:bg-accent/50 cursor-pointer"
                  >
                    <RegionIcons code="ID" />
                    <span>Indonesia</span>
                  </Link>
                  {OTHER_FREE_FIRE_REGIONS.map((item) => (
                    <Link
                      key={item.code}
                      href={item.href}
                      className="flex min-h-12 items-center justify-start gap-2 rounded-md border border-border/70 bg-background px-3 py-2.5 text-left text-xs font-semibold text-foreground transition-all duration-200 hover:border-primary/40 hover:bg-accent/50"
                    >
                      <RegionIcons code={item.code} />
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>

          {offersList.length > 0 && (
            <ScrollReveal delay={0.15}>
              <div className="max-w-6xl mx-auto px-4 mt-8">
                {offersList.map((offer) => (
                  <OfferCountdownBanner key={offer.id} offer={offer} />
                ))}
              </div>
            </ScrollReveal>
          )}

          <div className="max-w-6xl mx-auto px-4 pb-16">
            {offers.length > 0 && (
              <ScrollReveal delay={0.08}>
                <div id="offers">
                  <ProductSection
                    title={
                      locale === "ta"
                        ? "ஒருமுறை சலுகை"
                        : locale === "si"
                          ? "එක්වර දීමනාව"
                          : "One Time Offer"
                    }
                    products={offers}
                    cart={cartHook.cart}
                    stockAvailability={stockAvailability}
                    feasibility={stockEngine.feasibility}
                    effectiveMaxQty={cartHook.effectiveMaxQty}
                    onAdd={cartHook.addToCart}
                    onRemove={cartHook.removeFromCart}
                    onUpdateQty={cartHook.updateQty}
                    backgroundColor="#F2F8FF"
                    textColor="#5A98D8"
                    accentColor="#5A98D8"
                    iconSrc="/assets/box3.svg"
                    onNoPlayer={onNoPlayer}
                    isSpecialOffer={true}
                  />
                </div>
              </ScrollReveal>
            )}

            {subscriptions.length > 0 && (
              <ScrollReveal delay={0.1}>
                <div id="subscriptions">
                  <ProductSection
                    title="Subscriptions & Bundles"
                    products={subscriptions}
                    cart={cartHook.cart}
                    stockAvailability={effectiveStockAvailability}
                    feasibility={stockEngine.feasibility}
                    effectiveMaxQty={cartHook.effectiveMaxQty}
                    onAdd={cartHook.addToCart}
                    onRemove={cartHook.removeFromCart}
                    onUpdateQty={cartHook.updateQty}
                    backgroundColor="#F2F8FF"
                    textColor="#5A98D8"
                    accentColor="#5A98D8"
                    iconSrc="/assets/box3.svg"
                    onNoPlayer={onNoPlayer}
                  />
                </div>
              </ScrollReveal>
            )}

            {diamonds.length > 0 && (
              <ScrollReveal delay={0.1}>
                <div id="diamonds">
                  <ProductSection
                    title="Diamonds"
                    products={diamonds}
                    cart={cartHook.cart}
                    stockAvailability={stockAvailability}
                    feasibility={stockEngine.feasibility}
                    effectiveMaxQty={cartHook.effectiveMaxQty}
                    onAdd={cartHook.addToCart}
                    onRemove={cartHook.removeFromCart}
                    onUpdateQty={cartHook.updateQty}
                    backgroundColor="#F2F8FF"
                    textColor="#5A98D8"
                    accentColor="#5A98D8"
                    iconSrc="/assets/box3.svg"
                    onNoPlayer={onNoPlayer}
                  />
                </div>
              </ScrollReveal>
            )}
            {levelUpPasses.length > 0 && (
              <ScrollReveal delay={0.1}>
                <div id="level-up-pass">
                  <ProductSection
                    title="Level Up Pass"
                    products={levelUpPasses}
                    cart={cartHook.cart}
                    stockAvailability={stockAvailability}
                    feasibility={stockEngine.feasibility}
                    effectiveMaxQty={cartHook.effectiveMaxQty}
                    onAdd={cartHook.addToCart}
                    onRemove={cartHook.removeFromCart}
                    onUpdateQty={cartHook.updateQty}
                    backgroundColor="#F2F8FF"
                    textColor="#5A98D8"
                    accentColor="#5A98D8"
                    iconSrc="/assets/box3.svg"
                    onNoPlayer={onNoPlayer}
                  />
                </div>
              </ScrollReveal>
            )}
          </div>

          <ScrollReveal>
            <section
              className="px-4 pb-4 pt-8"
              aria-labelledby="game-topups-heading"
            >
              <div className="relative mx-auto max-w-6xl overflow-hidden rounded-lg border border-[#CFE3F8] bg-[#F2F8FF] px-5 py-10 shadow-sm sm:px-10 sm:py-14">
                <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-[#3083E8]/10 blur-2xl" />
                <div className="relative grid items-center gap-10 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
                  <div className="text-left">
                    <span className="mb-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-[#3083E8]">
                      <span className="h-1.5 w-1.5 bg-[#3083E8]" />
                      More than Free Fire
                    </span>
                    <h2
                      id="game-topups-heading"
                      className="font-display text-[38px] leading-[0.95] text-[#343434] md:text-[56px]"
                    >
                      GAME TOP-UPS{" "}
                      <span className="text-[#3083E8]">IN SRI LANKA</span>
                    </h2>
                    <p className="mt-4 max-w-xl text-[15px] font-medium leading-7 text-slate-600 md:text-base">
                      Buy game credits in LKR using secure local payments and
                      get fast delivery. Top up Free Fire diamonds or choose
                      from the most popular mobile and PC games.
                    </p>
                    <p className="mt-3 max-w-xl text-sm leading-6 text-slate-500">
                      Select your game and account region, enter the requested
                      player details, and choose your package. No game password
                      is required for supported direct top-ups.
                    </p>
                    <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <span>Secure payments</span>
                      <span className="text-[#79AEE5]">•</span>
                      <span>Fast delivery</span>
                      <span className="text-[#79AEE5]">•</span>
                      <span>LKR prices</span>
                    </div>
                  </div>
                  <nav
                    aria-label="Popular game top-up pages"
                    className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2"
                  >
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/free-fire"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/freefire-my.webp"
                          alt="Free Fire"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Free Fire
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/pubg-mobile"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/pubgm-global.webp"
                          alt="PUBG Mobile"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        PUBG Mobile
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/mobile-legend?region=sg"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/mlbb-my.webp"
                          alt="Mobile Legends"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Mobile Legends
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/blood-strike"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/bs-global.webp"
                          alt="Blood Strike"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Blood Strike
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/valorant-pc"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/valorant-my.webp"
                          alt="Valorant"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Valorant
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/call-of-duty-mobile-garena"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/codm-garena-my.webp"
                          alt="Call of Duty Mobile"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Call of Duty Mobile
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/8-ball-pool"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/8-ball-pool.webp"
                          alt="8 Ball Pool"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        8 Ball Pool
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="group flex min-h-12 items-center justify-between rounded-md border border-white bg-white px-3.5 font-display text-sm text-slate-800 shadow-sm transition hover:border-[#8DBBEA] hover:text-[#3083E8]"
                      href="/games/asphalt-9-legends"
                    >
                      <span className="flex items-center gap-2.5">
                        <Image
                          src="/images/games/asphalt-9-legends.webp"
                          alt="Asphalt 9"
                          width={32}
                          height={32}
                          className="h-8 w-8 rounded object-cover"
                        />
                        Asphalt 9
                      </span>
                      <span className="text-[#8DBBEA] transition group-hover:translate-x-0.5">
                        →
                      </span>
                    </Link>
                    <Link
                      className="col-span-2 mt-1 flex items-center justify-end gap-2 text-sm font-bold text-[#3083E8] hover:underline sm:col-span-3 lg:col-span-2"
                      href="/games"
                    >
                      View all games →
                    </Link>
                  </nav>
                </div>
                <div className="relative mt-10 grid border-t border-[#CFE3F8] pt-8 sm:grid-cols-3">
                  <div className="border-b border-[#CFE3F8] py-5 sm:border-b-0 sm:border-r sm:px-6 sm:py-1 sm:first:pl-0">
                    <span className="font-display text-xl text-[#3083E8]">
                      01
                    </span>
                    <h3 className="mt-2 font-display text-lg text-slate-900">
                      Choose your game
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-500">
                      Browse the full catalog and select the correct account
                      region.
                    </p>
                  </div>
                  <div className="border-b border-[#CFE3F8] py-5 sm:border-b-0 sm:border-r sm:px-6 sm:py-1">
                    <span className="font-display text-xl text-[#3083E8]">
                      02
                    </span>
                    <h3 className="mt-2 font-display text-lg text-slate-900">
                      Select a package
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-500">
                      Compare clear LKR prices and pick the top-up that suits
                      you.
                    </p>
                  </div>
                  <div className="py-5 sm:px-6 sm:py-1 sm:last:pr-0">
                    <span className="font-display text-xl text-[#3083E8]">
                      03
                    </span>
                    <h3 className="mt-2 font-display text-lg text-slate-900">
                      Pay securely
                    </h3>
                    <p className="mt-1.5 text-sm leading-6 text-slate-500">
                      Complete payment using available local or card payment
                      methods.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </ScrollReveal>

          <ScrollReveal>
            <section className="relative py-16 px-4">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2
                    className={`font-display leading-[1] text-center mb-3 ${
                      locale === "si"
                        ? "text-[28px] md:text-[38px]"
                        : locale === "ta"
                          ? "text-[32px] md:text-[44px]"
                          : "text-[34px] md:text-[48px]"
                    }`}
                    style={{ color: "hsl(var(--foreground))" }}
                  >
                    {locale === "si" ? (
                      <>
                        <span style={{ color: "#343434" }}>
                          BAASIL TOPUP&nbsp;
                        </span>
                        <span
                          className="font-sinhala-title"
                          style={{
                            color: "#3083E8",
                            fontSize: "1em",
                            fontWeight: 400,
                            letterSpacing: "0.01em",
                          }}
                        >
                          තෝරා ගන්න ඕන ඇයි?
                        </span>
                      </>
                    ) : locale === "en" ? (
                      <>
                        <span style={{ color: "#343434" }}>
                          WHY CHOOSE&nbsp;
                        </span>
                        <span style={{ color: "#3083E8" }}>BAASIL TOPUP</span>
                        <span style={{ color: "#343434" }}>?</span>
                      </>
                    ) : (
                      <>
                        Why Choose{" "}
                        <span style={{ color: "#3083E8" }}>Baasil Topup</span>?
                      </>
                    )}
                  </h2>
                  <p className="text-slate-500 max-w-xl mx-auto">
                    {locale === "si" ? (
                      <span className="font-sinhala text-sm md:text-base">
                        ශ්‍රී ලංකාවේ ලාභම හා #1 FF topup service{" "}
                      </span>
                    ) : locale === "ta" ? (
                      "இலங்கையில் 10000+ வீரர்கள் நம்பும் #1 FF topup சேவை"
                    ) : (
                      "Sri Lanka's #1 trusted FF topup service trusted by 10000+ gamers"
                    )}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {[
                    {
                      title:
                        locale === "si"
                          ? "Seconds ඇතුළත Delivery"
                          : locale === "ta"
                            ? "செக்கன்களில் டெலிவரி"
                            : "Instant Delivery in Seconds",
                      desc:
                        locale === "si"
                          ? "Payment confirm වූ විගස ඔබේ game account එකට diamonds automatic ලෙස arrive වෙනවා."
                          : locale === "ta"
                            ? "கட்டணம் உறுதியான உடனே உங்கள் game கணக்கிற்கு டயமண்டுகள் தானாக வந்து சேரும்."
                            : "Diamonds arrive automatically in your game account as soon as payment is confirmed.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M13 10V3L4 14h7v7l9-11h-7z"
                          />
                        </svg>
                      ),
                    },
                    {
                      title:
                        locale === "si"
                          ? "Password අවශ්‍ය නැත"
                          : locale === "ta"
                            ? "கடவுச்சொல் தேவையில்லை"
                            : "No Password Required",
                      desc:
                        locale === "si"
                          ? "ඔබේ Player UID එක පමණයි ඕනෑ. Password, email, login details කිසිවක් ඕනෑ නැත."
                          : locale === "ta"
                            ? "உங்கள் Player UID மட்டுமே தேவை. கடவுச்சொல், மின்னஞ்சல் எதுவும் தேவையில்லை."
                            : "Only your Player UID is needed. No password, email, or login details required.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                          />
                        </svg>
                      ),
                    },
                    {
                      title:
                        locale === "si"
                          ? "ශ්‍රී ලංකාවේ අඩුම මිල"
                          : locale === "ta"
                            ? "இலங்கையின் மலிவான விலை"
                            : "Cheapest in Sri Lanka",
                      desc:
                        locale === "si"
                          ? "Baasil Topup හි ශ්‍රී ලංකාවේ ඕනෑම topup site එකකට වඩා අඩු මිලට FF diamonds ලබා ගත හැකිය."
                          : locale === "ta"
                            ? "Baasil Topup இல் இலங்கையின் எந்த topup தளத்தையும் விட மலிவான விலையில் FF டயமண்டுகள் பெறலாம்."
                            : "Get FF diamonds at lower prices than any other topup site in Sri Lanka.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      ),
                    },
                    {
                      title: "EzCash & LankaQR",
                      desc:
                        locale === "si"
                          ? "EzCash, LankaQR (Genie QR), Visa, MasterCard, Binance Pay, Bybit Pay හෝ bank transfer - ඔබට පහසු ඕනෑම ක්‍රමයකින් ගෙවන්න."
                          : locale === "ta"
                            ? "EzCash, LankaQR (Genie QR), Visa, MasterCard, Binance Pay, Bybit Pay அல்லது bank transfer - உங்களுக்கு வசதியான எந்த முறையிலும் செலுத்துங்கள்."
                            : "Pay via EzCash, LankaQR (Genie QR), Visa, MasterCard, Binance Pay, Bybit Pay, or bank transfer - whatever is easiest for you.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                          />
                        </svg>
                      ),
                    },
                    {
                      title:
                        locale === "si"
                          ? "ශ්‍රී ලාංකීය Registered Business"
                          : locale === "ta"
                            ? "இலங்கை பதிவு செய்யப்பட்ட வணிகம்"
                            : "Registered Sri Lankan Business",
                      desc:
                        locale === "si"
                          ? "Baasil Topup ශ්‍රී ලංකාවේ registered business එකක්. 10000+ gamers විශ්වාස කරන #1 topup service."
                          : locale === "ta"
                            ? "Baasil Topup இலங்கையில் பதிவு செய்யப்பட்ட வணிகம். 10000+ வீரர்கள் நம்பும் #1 topup சேவை."
                            : "Baasil Topup is a registered business in Sri Lanka. The #1 topup service trusted by 10000+ gamers.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                          />
                        </svg>
                      ),
                    },
                    {
                      title: "24/7 Support",
                      desc:
                        locale === "si"
                          ? "දිනයේ ඕනෑම වේලාවක ගැටලු ඇත්නම් WhatsApp මගින් +94742300407 අමතන්න."
                          : locale === "ta"
                            ? "நாளின் எந்த நேரத்திலும் பிரச்சினைகள் இருந்தால் WhatsApp மூலம் +94742300407 ஐ தொடர்பு கொள்ளவும்."
                            : "Contact us anytime via WhatsApp at +94742300407 if you have any issues.",
                      icon: (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                          />
                        </svg>
                      ),
                    },
                  ].map(({ title, desc, icon }) => (
                    <div
                      key={title}
                      className="group bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#5A98D8]/10 flex items-center justify-center text-[#5A98D8] shrink-0 group-hover:scale-110 transition-transform duration-200">
                          {icon}
                        </div>
                        <h3 className="font-semibold text-base text-slate-900">
                          {title}
                        </h3>
                      </div>
                      <p className="text-slate-500 text-sm leading-relaxed">
                        {desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </ScrollReveal>

          <DeferredSection minHeight={560}>
            <Testimonials locale={locale} />
          </DeferredSection>
          <DeferredSection minHeight={760}>
            <FAQSection locale={locale} />
          </DeferredSection>
          <DeferredSection minHeight={720}>
            <Leaderboard locale={locale} />
          </DeferredSection>
          <DeferredSection minHeight={620}>
            <Footer locale={locale} />
          </DeferredSection>

          <span className="cart-wrapper">
            <FloatingCart
              itemCount={cartHook.cartQtyTotal}
              uid={uid}
              validationResult={validationResult}
            >
              <CartSidebar
                cart={cartHook.cart}
                products={products}
                channelLevelUps={[]}
                stockAvailability={stockAvailability}
                feasibility={stockEngine.feasibility}
                subtotal={cartHook.subtotal}
                referralDiscount={cartHook.referralDiscount}
                total={cartHook.total}
                referralBalance={0}
                useReferralBalance={false}
                setUseReferralBalance={() => {}}
                isAuthenticated={auth.isAuthenticated}
                hasMobile={auth.hasMobile}
                validationResult={validationResult}
                isSubmittingOrder={isSubmittingOrder}
                hasOutOfStockItems={cartHook.hasOutOfStockItems}
                outOfStockNames={cartHook.outOfStockNames}
                onClear={cartHook.clearCart}
                onUpdateQty={cartHook.updateQty}
                onRemove={cartHook.removeFromCart}
                onCheckout={handleCheckout}
                isMobile={true}
                paymentConfig={paymentConfig}
                charges={charges}
                activeOffers={activeOffers}
              />
            </FloatingCart>
          </span>

          {showPaymentModal && (
            <PaymentModal
              show={showPaymentModal}
              baseAmount={cartHook.totalBeforeCharge}
              charges={charges}
              paymentConfig={paymentConfig}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={setSelectedPaymentMethod}
              isSubmittingOrder={isSubmittingOrder}
              onClose={() => setShowPaymentModal(false)}
              onGenieCheckout={handleGenieCheckout}
              onCeypayCheckout={handleCeypayCheckout}
              onBcoinCheckout={handleBcoinCheckout}
              onEzCashCheckout={handleEzCashCheckout}
            />
          )}

          {pendingGenieOrder && (
            <QrCodeModal
              isOpen={!!pendingGenieOrder}
              onClose={() => setPendingGenieOrder(null)}
              orderData={pendingGenieOrder}
            />
          )}

          {pendingCeypayOrder && (
            <QrCodeModal
              isOpen={!!pendingCeypayOrder}
              onClose={() => setPendingCeypayOrder(null)}
              orderData={pendingCeypayOrder}
            />
          )}
        </>
      )}

      {isLoginModalOpen && (
        <LoginModal
          key="landing-page-login-modal"
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            auth.refreshSession();
          }}
        />
      )}
      {isMobileModalOpen && (
        <MobileNumberModal
          isOpen={isMobileModalOpen}
          onClose={() => setIsMobileModalOpen(false)}
        />
      )}
    </div>
  );
};

export default NewUiLanding;
